import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SysService } from 'src/app/modules/sys/services/sys.service';
import { forkJoin } from 'rxjs';
import { ImageUrlPipe } from '../../../../../pipe/image-url.pipe';

const ROWS_PER_PAGE = 15; // fewer rows per page since A3 landscape has many columns

@Component({
    selector: 'app-timesheet-report-viewer',
    standalone: true,
    imports: [CommonModule, ImageUrlPipe],
    templateUrl: './timesheet-report-viewer.component.html',
    styleUrl: './timesheet-report-viewer.component.scss',
})
export class TimesheetReportViewerComponent implements OnInit {

    data: any | null = null;
    pages: any[][] = [];
    dayColumns: string[] = [];

    today = new Date().toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    companyName: string = '';
    companyLogo: string = '';
    companyLogoBase64: string = '';
    constructor(private sysService: SysService) { }

    ngOnInit(): void {
        const raw = sessionStorage.getItem('timesheetReportData');

        if (!raw) {
            console.warn('No timesheet report data found');
            return;
        }

        try {
            const parsed = JSON.parse(raw);

            if (!parsed || parsed.length === 0) {
                console.warn('Empty timesheet data');
                return;
            }

            const firstRow = parsed[0];

            // Extract metadata from first row
            this.data = {
                monthName: firstRow.monthName,
                year: firstRow.year,
                month: firstRow.month,
                periodFrom: firstRow.periodFrom,
                periodTo: firstRow.periodTo,
                rows: parsed,
            };

            // Extract day columns from first row
            if (firstRow.days && firstRow.days.length > 0) {
                this.dayColumns = firstRow.days.map((d: any) => d.dayLabel);
            }

            this.paginate(parsed);
        } catch (e) {
            console.error('Error parsing timesheet report data', e);
        }

        this.getConfigKeys();

        setTimeout(() => {
            const page = document.querySelector('.page') as HTMLElement;
            if (page) {
                const rect = page.getBoundingClientRect();
           
                // Should show approximately 420.0 x 297.0
            }
        }, 500);
    }

    getConfigKeys(): void {
        forkJoin({
            name: this.sysService.getConfigByKey('COMPANY_NAME'),
            logo: this.sysService.getConfigByKey('COMPANY_LOGO'),
        }).subscribe(({ name, logo }) => {
            if (name.isSuccess) {
                this.companyName = name.data.configValue;
            }
            if (logo.isSuccess) {
                this.companyLogo = logo.data.configValue;
                // ⭐ Convert logo to base64 for PDF export
                this.convertLogoToBase64();
            }
        });
    }

    private convertLogoToBase64(): void {
        // Wait for the image to render in DOM, then convert
        setTimeout(() => {
            const imgEl = document.querySelector('.company-logo') as HTMLImageElement;
            if (imgEl && imgEl.complete) {
                this.toBase64(imgEl);
            } else if (imgEl) {
                imgEl.onload = () => this.toBase64(imgEl);
            }
        }, 1000);
    }

    private toBase64(img: HTMLImageElement): void {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.drawImage(img, 0, 0);
                this.companyLogoBase64 = canvas.toDataURL('image/png');
            }
        } catch (e) {
            console.warn('Could not convert logo to base64:', e);
        }
    }

    private paginate(rows: any[]): void {
        this.pages = [];
        for (let i = 0; i < rows.length; i += ROWS_PER_PAGE) {
            this.pages.push(rows.slice(i, i + ROWS_PER_PAGE));
        }
    }

    // ===================== HELPERS =====================

    getDayValue(row: any, dayLabel: string): string {
        if (!row.days) return '';
        const entry = row.days.find((d: any) => d.dayLabel === dayLabel);
        return entry && entry.value != null && entry.value !== '' ? String(entry.value) : '';
    }

    getCellClass(value: string): string {
        if (!value || value.trim() === '') return '';
        switch (value.toUpperCase()) {
            case 'WKD': return 'status-wkd';
            case 'ABS': return 'status-abs';
            case 'EXC': return 'status-exc';
            case 'CHK': return 'status-chk';
            case 'SKL': return 'status-skl';
            case 'LV': return 'status-lv';
            default:
                return !isNaN(Number(value)) ? 'status-hours' : '';
        }
    }

    // ===================== PRINT & PDF =====================

    print(): void {
        // Inject A3 landscape print styles
        const style = document.createElement('style');
        style.id = 'print-a3-override';
        style.innerHTML = `
    @media print {
      @page {
        size: 420mm 297mm !important;
        margin: 5mm !important;
      }
      body {
        width: 420mm !important;
      }
    }
  `;
        document.head.appendChild(style);

        setTimeout(() => {
            window.print();
            // Clean up
            setTimeout(() => {
                const el = document.getElementById('print-a3-override');
                if (el) document.head.removeChild(el);
            }, 1000);
        }, 100);
    }

    async downloadPdf(): Promise<void> {
        const { default: jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');

        // ⭐ A3 Landscape
        const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a3'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();   // 420
        const pageHeight = pdf.internal.pageSize.getHeight();  // 297

        // ── Colors ──────────────────────────────────────────────
        const headerBg: [number, number, number] = [26, 35, 64];       // #1a2340
        const headerText: [number, number, number] = [255, 255, 255];
        const statusColors: Record<string, { bg: [number, number, number]; text: [number, number, number] }> = {
            'WKD': { bg: [232, 245, 233], text: [46, 125, 50] },
            'ABS': { bg: [255, 235, 238], text: [198, 40, 40] },
            'EXC': { bg: [255, 243, 224], text: [230, 81, 0] },
            'CHK': { bg: [227, 242, 253], text: [21, 101, 192] },
            'SKL': { bg: [243, 229, 245], text: [106, 27, 154] },
            'LV': { bg: [239, 235, 233], text: [121, 85, 72] },
            'UPL': { bg: [255, 243, 224], text: [230, 81, 0] },
        };

        const isNumeric = (val: string) => !isNaN(Number(val)) && val.trim() !== '';

        // ── Build table headers ─────────────────────────────────
        const tableHeaders = [
            'ID',
            'Employee Name',
            ...this.dayColumns,
            'Hours',
            'Late',
            'OT'
        ];

        // ── Build table body ────────────────────────────────────
        const tableBody = this.data.rows.map((row: any) => {
            const dayValues = this.dayColumns.map(label => this.getDayValue(row, label));
            return [
                row.employeeId,
                row.employeeNameEn,
                ...dayValues,
                row.totalWorkHours,
                row.totalLate,
                row.totalOT
            ];
        });

        // ── Add header info on first page ───────────────────────
        const addPageHeader = (pageNum: number) => {
            // Left - Meta
            pdf.setFontSize(9);
            pdf.setTextColor(90, 100, 128);
            pdf.text(`Month: ${this.data.monthName} ${this.data.year}`, 10, 12);
            pdf.text(`From: ${new Date(this.data.periodFrom).toLocaleDateString('en-GB')}`, 10, 17);
            pdf.text(`To: ${new Date(this.data.periodTo).toLocaleDateString('en-GB')}`, 10, 22);

            // Center - Company name & title
            pdf.setFontSize(14);
            pdf.setTextColor(26, 35, 64);
            pdf.text(this.companyName || 'Company', pageWidth / 2, 12, { align: 'center' });

            pdf.setFontSize(10);
            pdf.setTextColor(42, 58, 96);
            pdf.text('EMPLOYEE MONTHLY TIMESHEET REPORT', pageWidth / 2, 18, { align: 'center' });

            // Logo (if base64 available)
            if (this.companyLogoBase64) {
                try {
                    pdf.addImage(this.companyLogoBase64, 'PNG', pageWidth - 30, 5, 20, 18);
                } catch (e) {
                    // Skip logo if error
                }
            }
        };

        // ── Day column width calculation ────────────────────────
        const idWidth = 12;
        const nameWidth = 35;
        const totalColWidth = 16;
        const fixedWidth = idWidth + nameWidth + (totalColWidth * 3); // ID + Name + 3 totals
        const remainingWidth = pageWidth - fixedWidth - 16; // 16mm margins
        const dayColWidth = Math.max(8, remainingWidth / this.dayColumns.length);

        // ── Column styles ───────────────────────────────────────
        const columnStyles: Record<number, any> = {
            0: { cellWidth: idWidth, halign: 'center', fontSize: 6 },   // ID
            1: { cellWidth: nameWidth, halign: 'left', fontSize: 6, overflow: 'ellipsize' },  // Name
        };

        // Day columns
        this.dayColumns.forEach((_, i) => {
            columnStyles[i + 2] = {
                cellWidth: dayColWidth,
                halign: 'center',
                fontSize: 5.5,
            };
        });

        // Total columns (Hours, Late, OT)
        const totalStartIdx = this.dayColumns.length + 2;
        columnStyles[totalStartIdx] = { cellWidth: totalColWidth, halign: 'center', fontSize: 6 };
        columnStyles[totalStartIdx + 1] = { cellWidth: totalColWidth, halign: 'center', fontSize: 6 };
        columnStyles[totalStartIdx + 2] = { cellWidth: totalColWidth, halign: 'center', fontSize: 6 };

        // ── Generate table ──────────────────────────────────────
        let currentPage = 0;

        autoTable(pdf, {
            head: [tableHeaders],
            body: tableBody,
            startY: 26,
            margin: { top: 26, left: 8, right: 8, bottom: 12 },
            theme: 'grid',
            styles: {
                fontSize: 6,
                cellPadding: 1.5,
                lineWidth: 0.1,
                lineColor: [200, 200, 200],
                overflow: 'ellipsize',
            },
            headStyles: {
                fillColor: headerBg,
                textColor: headerText,
                fontSize: 5.5,
                fontStyle: 'bold',
                halign: 'center',
                cellPadding: 2,
            },
            columnStyles: columnStyles,
            alternateRowStyles: {
                fillColor: [247, 249, 252],
            },

            // ⭐ Color cells based on status
            didParseCell: (data: any) => {
                if (data.section === 'body') {
                    const colIdx = data.column.index;
                    const value = String(data.cell.raw ?? '').trim().toUpperCase();

                    // Day columns
                    if (colIdx >= 2 && colIdx < 2 + this.dayColumns.length) {
                        const colors = statusColors[value];
                        if (colors) {
                            data.cell.styles.fillColor = colors.bg;
                            data.cell.styles.textColor = colors.text;
                            data.cell.styles.fontStyle = 'bold';
                        } else if (isNumeric(value) && Number(value) > 0) {
                            data.cell.styles.fillColor = [224, 242, 241];
                            data.cell.styles.textColor = [0, 105, 92];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }

                    // Late column (negative = red)
                    if (colIdx === totalStartIdx + 1) {
                        const num = Number(data.cell.raw);
                        if (num < 0) {
                            data.cell.styles.textColor = [198, 40, 40];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }

                    // OT column (positive = green)
                    if (colIdx === totalStartIdx + 2) {
                        const num = Number(data.cell.raw);
                        if (num > 0) {
                            data.cell.styles.textColor = [46, 125, 50];
                            data.cell.styles.fontStyle = 'bold';
                        }
                    }
                }
            },

            // ⭐ Add header & footer on each page
            didDrawPage: (data: any) => {
                currentPage++;
                addPageHeader(currentPage);

                // Footer
                pdf.setFontSize(7);
                pdf.setTextColor(150, 150, 150);
                const pageCount = (pdf as any).internal.getNumberOfPages();
                pdf.text(
                    new Date().toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }),
                    10,
                    pageHeight - 5
                );
                pdf.text(
                    `${currentPage} of ${pageCount}`,
                    pageWidth - 20,
                    pageHeight - 5
                );
            }
        });

        const filename = `monthly_timesheet_${this.data.monthName}_${this.data.year}.pdf`;
        pdf.save(filename);
    }
}